import { apiClient } from "../apiClient";
import type {
  Agent,
  AgentsResponse,
  AgentsByConversationsResponse,
  AgentConversationItem,
} from "@interfaces/api.interface";

const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

const MAX_EXTERNAL_IDS_PER_REQUEST = 100;

export interface GetAgentsParams {
  offset?: number;
  limit?: number;
}

export const getAgentsList = async (
  params: GetAgentsParams = {}
): Promise<{ agents: Agent[]; total: number }> => {
  const { offset = 0, limit = 20 } = params;
  const response = await apiClient.get<AgentsResponse>(
    `/api/agents/list?offset=${offset}&limit=${limit}`
  );

  if (response.code === 0 && response.data) {
    return {
      agents: response.data.agents || [],
      total: response.data.total || 0,
    };
  }

  throw new Error(response.message || "Failed to fetch agents");
};

export interface AssignAgentParams {
  agentId: number;
  externalId: string;
  platform: string;
  pageId?: string;
}

export interface AssignAgentResponse {
  code: number;
  data?: unknown;
  message: string;
}

export const assignAgentToConversation = async (
  params: AssignAgentParams
): Promise<AssignAgentResponse> => {
  const { agentId, externalId, platform, pageId } = params;

  try {
    const requestBody: {
      agentId: number;
      externalId: string;
      platform: string;
      pageId?: string;
    } = {
      agentId,
      externalId,
      platform,
    };

    if (platform === "Facebook" && pageId) {
      requestBody.pageId = pageId;
    }

    const response = await apiClient.post<AssignAgentResponse>(
      "/api/agents/assign",
      requestBody
    );

    return response;
  } catch (error) {
    console.error("Error assigning agent to conversation:", error);
    throw error;
  }
};

export interface UnassignAgentParams {
  externalId: string;
  platform: string;
  pageId?: string;
}

export interface UnassignAgentResponse {
  code: number;
  data?: unknown;
  message: string;
}

export const unassignAgentFromConversation = async (
  params: UnassignAgentParams
): Promise<UnassignAgentResponse> => {
  const { externalId, platform, pageId } = params;

  try {
    const requestBody: {
      externalId: string;
      platform: string;
      pageId?: string;
    } = {
      externalId,
      platform,
    };

    if (platform === "Facebook" && pageId) {
      requestBody.pageId = pageId;
    }

    const response = await apiClient.post<UnassignAgentResponse>(
      "/api/agents/unassign",
      requestBody
    );

    return response;
  } catch (error) {
    console.error("Error unassigning agent from conversation:", error);
    throw error;
  }
};

export interface GetAgentsByConversationsParams {
  externalIds: string[];
  platform: string;
  pageId: string;
}

export const getAgentsByConversations = async (
  params: GetAgentsByConversationsParams
): Promise<{ total: number; items: AgentConversationItem[] }> => {
  const { externalIds, platform, pageId } = params;

  try {
    const chunks = chunkArray(externalIds, MAX_EXTERNAL_IDS_PER_REQUEST);
    
    const allItems: AgentConversationItem[] = [];
    const promises = chunks.map((chunk) =>
      apiClient.post<AgentsByConversationsResponse>(
        "/api/agents/conversation",
        {
          externalIds: chunk,
          platform,
          pageId,
        }
      )
    );

    const responses = await Promise.all(promises);
    
    responses.forEach((response) => {
      if (response.code === 0 && response.data?.items) {
        allItems.push(...response.data.items);
      }
    });

    return {
      total: allItems.length,
      items: allItems,
    };
  } catch (error) {
    console.error("Error fetching agents by conversations:", error);
    throw error;
  }
};

export interface ConversationHistoryItem {
  description: string;
  id: number;
  performedAt: number;
}

export interface ConversationHistoryResponse {
  code: number;
  data?: {
    histories: ConversationHistoryItem[];
    total: number;
  };
  message: string;
}

export interface GetConversationHistoryParams {
  externalId: string;
  platform: string;
  limit?: number;
  offset?: number;
  pageId?: string;
}

export const getConversationHistory = async (
  params: GetConversationHistoryParams
): Promise<{ histories: ConversationHistoryItem[]; total: number }> => {
  const { externalId, platform, limit = 20, offset = 0, pageId } = params;

  try {
    const requestBody: {
      externalId: string;
      platform: string;
      limit: number;
      offset: number;
      pageId?: string;
    } = {
      externalId,
      platform,
      limit,
      offset,
    };

    if (platform === "Facebook" && pageId) {
      requestBody.pageId = pageId;
    }

    const response = await apiClient.post<ConversationHistoryResponse>(
      "/api/agents/history/conversation",
      requestBody
    );

    if (response.code === 0 && response.data) {
      return {
        histories: response.data.histories || [],
        total: response.data.total || 0,
      };
    }

    throw new Error(response.message || "Failed to fetch conversation history");
  } catch (error) {
    console.error("Error fetching conversation history:", error);
    throw error;
  }
};
